/* Duskhaven bundle tiers (2026-09-29). Block: blocks/dh-bundle-tiers.liquid. Loaded as a module by
   snippets/dh-pdp-assets.liquid.
   <dh-bundle-tiers>:
   - choosing a row sets the product form's quantity through Horizon's quantity selector, which also
     fires quantity-selector:update so the sticky add-to-cart bar shows the same count;
   - changing the quantity by hand (+/-, typing) moves the highlight (4 or more keeps the top tier);
   - a variant change swaps in that variant's totals (all pre-formatted by Liquid's money filter,
     so they always match the shop's currency format). */
import { StandardEvents } from '@shopify/events';

const MAIN = '.product-information';
const QTY_EVENT = 'quantity-selector:update';

class DhBundleTiers extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    try {
      this.variants = JSON.parse(this.querySelector('[data-dh-bundle-variants]')?.textContent || '{}');
    } catch {
      this.variants = {};
    }
    this.radios = /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('.dh-bundle__radio')]);
    this.tiers = this.radios.map((r) => Number(r.value)).filter((n) => n > 0);

    this.addEventListener('change', this.onPick);
    document.addEventListener(QTY_EVENT, this.onQty);
    document.addEventListener('input', this.onType);
    document.addEventListener('change', this.onOption);
    document.addEventListener(StandardEvents.productSelect, this.onSelect);
    window.addEventListener('pageshow', this.onShow);

    this.showVariant(this.variantId());
    this.syncFromQuantity();
  }

  disconnectedCallback() {
    this._ready = false;
    this.removeEventListener('change', this.onPick);
    document.removeEventListener(QTY_EVENT, this.onQty);
    document.removeEventListener('input', this.onType);
    document.removeEventListener('change', this.onOption);
    document.removeEventListener(StandardEvents.productSelect, this.onSelect);
    window.removeEventListener('pageshow', this.onShow);
  }

  /* ---------- lookups (always fresh: Horizon morphs parts of the form on variant change) ---------- */
  get section() {
    return this.closest('.shopify-section') || document;
  }
  get form() {
    return this.section.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
  }
  get qtySelector() {
    return /** @type {any} */ (this.form?.querySelector('quantity-selector-component'));
  }
  get qtyInput() {
    return /** @type {HTMLInputElement | null} */ (this.form?.querySelector('input[name="quantity"]'));
  }
  variantId() {
    const input = /** @type {HTMLInputElement | null} */ (this.form?.querySelector('input[name="id"]'));
    return (input && input.value) || this.dataset.variantId || '';
  }

  /* ---------- events ---------- */
  onPick = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || !radio.classList.contains('dh-bundle__radio')) return;
    this.setQuantity(Number(radio.value));
  };

  onQty = (event) => {
    if (event.detail?.cartLine) return;
    const form = this.form;
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;
    this.highlight(Number(event.detail?.quantity));
  };

  onType = (event) => {
    if (event.target && event.target === this.qtyInput) {
      this.highlight(parseInt(/** @type {HTMLInputElement} */ (event.target).value, 10));
    }
  };

  onOption = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLElement) || !this.section.contains(input)) return;
    if (!input.closest(`${MAIN} variant-picker`)) return;
    const id =
      input instanceof HTMLSelectElement ? input.selectedOptions[0]?.dataset.variantId : input.dataset.variantId;
    if (id) this.showVariant(id);
  };

  onSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.section.contains(target)) return;
    if (target.closest('product-card, quick-add-component, dialog')) return;
    event.promise
      ?.then((result) => {
        const id = result?.detail?.resource?.id;
        this.showVariant(id ? String(id) : this.variantId());
        // Horizon keeps the chosen quantity across a variant change; re-read it in case it clamped.
        this.syncFromQuantity();
      })
      .catch(() => {});
  };

  onShow = () => this.syncFromQuantity();

  /* ---------- actions ---------- */
  /** @param {number} qty */
  setQuantity(qty) {
    const input = this.qtyInput;
    if (!input || !(qty > 0)) return;
    const max = parseInt(input.max, 10);
    const value = Number.isFinite(max) && max > 0 ? Math.min(qty, max) : qty;
    input.value = String(value);
    const selector = this.qtySelector;
    if (selector && typeof selector.onQuantityChange === 'function') {
      selector.updateButtonStates?.();
      // Dispatches quantity-selector:update: the sticky bar and our onQty listen for it.
      selector.onQuantityChange();
    } else {
      const update = new Event(QTY_EVENT, { bubbles: true });
      /** @type {any} */ (update).detail = { quantity: value };
      input.dispatchEvent(update);
    }
  }

  syncFromQuantity() {
    const input = this.qtyInput;
    if (input) this.highlight(parseInt(input.value, 10));
  }

  /** Checks the highest tier at or below the quantity (4+ keeps the top tier). @param {number} qty */
  highlight(qty) {
    if (!(qty > 0)) return;
    let pick = this.tiers[0];
    for (const tier of this.tiers) if (qty >= tier) pick = tier;
    for (const radio of this.radios) radio.checked = Number(radio.value) === pick;
  }

  /** @param {string} id */
  showVariant(id) {
    const data = id && this.variants[id];
    if (!data) return;
    this.dataset.variantId = id;
    this.querySelectorAll('[data-dh-field]').forEach((el) => {
      const value = data[/** @type {HTMLElement} */ (el).dataset.dhField || ''];
      if (typeof value === 'string' && el.textContent !== value) el.textContent = value;
    });
    this.classList.toggle('is-unavailable', data.a === false);
  }
}

if (!customElements.get('dh-bundle-tiers')) customElements.define('dh-bundle-tiers', DhBundleTiers);
